import { useTranslation } from 'react-i18next';

interface FooterProps {
  toggleLanguage: () => void;
}

export default function Footer({ toggleLanguage }: FooterProps) {
  const { i18n, t } = useTranslation();

  return (
    <footer className="bg-gradient-to-br from-[#B8D4E6] via-[#A8C8D8] to-[#9ABFD5] text-gray-800" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="md:col-span-1">
            <img 
              src="https://static.readdy.ai/image/4bdd8f0f3f64793b594ebaf891f5a54a/26ff1389f2f6e943de2bc36658d6bfba.png" 
              alt="Kimberlite Logo" 
              className="h-32 md:h-36 w-auto mb-6"
              style={{ 
                filter: 'brightness(1) contrast(1.1)',
                background: 'transparent',
                padding: '8px'
              }}
            />
          </div>

          {/* Company */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-900">
              {i18n.language === 'ar' ? 'الشركة' : 'Company'}
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#about" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'من نحن' : 'About Us'}
                </a>
              </li>
              <li>
                <a href="#faq" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'الأسئلة الشائعة' : 'FAQ'}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'الأخبار' : 'News'}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-900">
              {i18n.language === 'ar' ? 'الخدمات' : 'Services'}
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'العثور على موردين/مشترين موثوقين' : 'Finding Reliable Suppliers/Buyers'}
                </a>
              </li>
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'التفاوض على أفضل الشروط التجارية' : 'Negotiating Best Commercial Terms'}
                </a>
              </li>
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'إعداد العقود ومراقبة تنفيذها' : 'Contract Preparation & Monitoring'}
                </a>
              </li>
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'تقديم الاستشارات التجارية والسوقية' : 'Business & Market Consulting'}
                </a>
              </li>
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'تسهيل الشحن والتخليص الجمركي' : 'Shipping & Customs Clearance'}
                </a>
              </li>
              <li>
                <a href="#services" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'تأسيس الشركات والمعاملات المصرفية' : 'Company Setup & Banking Services'}
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-gray-900">
              {i18n.language === 'ar' ? 'الموارد' : 'Resources'}
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#regional" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'رؤى السوق' : 'Market Insights'}
                </a>
              </li>
              <li>
                <a href="#regional" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'أدلة التجارة' : 'Trade Guides'}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                  {i18n.language === 'ar' ? 'اتصل بنا' : 'Contact'}
                </a>
              </li>
            </ul>
          </div>

          {/* Copyright & Social */}
          <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
            <p className="text-lg font-serif mb-4 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              {i18n.language === 'ar' ? 'كيمبرلايت للوساطة التجارية © 2026' : 'Kimberlite Commercial Brokerage © 2026'}
            </p>
            <p className="text-sm text-gray-700 mb-4">
              {i18n.language === 'ar' ? 'جميع الحقوق محفوظة' : 'All Rights Reserved'}
            </p>
            <div className={`flex gap-4 ${i18n.language === 'ar' ? 'justify-end' : 'justify-start'}`}>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill text-xl"></i>
              </a>
              <a href="mailto:info@kimberlite.qa" className="text-gray-700 hover:text-[#1A5F7A] transition-colors cursor-pointer">
                <i className="ri-mail-fill text-xl"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid md:grid-cols-3 gap-8 items-center pt-8 border-t border-gray-400">
          <div className="rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/86f082f828dce9871c3c14698a79d2a5.jpg"
              alt="Doha Night"
              className="w-full h-32 object-cover object-top"
            />
          </div>

          <div className="text-center">
            <p className="text-2xl font-serif text-gray-800 leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
              <div>{i18n.language === 'ar' ? 'التميز في' : 'Excellence in'}</div>
              <div>{i18n.language === 'ar' ? 'الوساطة' : 'Commercial'}</div>
              <div>{i18n.language === 'ar' ? 'التجارية' : 'Brokerage'}</div>
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-700 mb-3">
              {i18n.language === 'ar' ? 'ابق على اطلاع باتجاهات السوق' : 'Stay informed about market trends'}
            </p>
            <p className="text-sm text-gray-700 mb-3">
              {i18n.language === 'ar' ? 'وفرص التجارة' : 'and trade opportunities'}
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder={i18n.language === 'ar' ? 'بريدك الإلكتروني' : 'Your email'}
                className="flex-1 px-4 py-2 bg-white/50 border border-gray-400 rounded-full text-sm focus:outline-none focus:border-[#1A5F7A] transition-colors"
              />
              <button className="px-6 py-2 bg-[#1A5F7A] text-white rounded-full text-sm font-medium hover:bg-[#134A5E] transition-all cursor-pointer whitespace-nowrap flex items-center gap-2">
                {i18n.language === 'ar' ? 'اشترك' : 'Subscribe'}
                <i className={`ri-arrow-${i18n.language === 'ar' ? 'left' : 'right'}-line`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
